import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  FileText, ArrowRight, Check, DollarSign, Send, 
  CheckCircle, CreditCard, Clock, RefreshCw, Sparkles, MessageSquare
} from 'lucide-react';

const EstimatesInvoicingPage = () => {
  const estimateFeatures = [
    {
      icon: FileText,
      title: "Professional Estimates",
      description: "Create detailed estimates with line items, pricing, and service descriptions that look great on any device."
    },
    {
      icon: Send,
      title: "Instant Delivery",
      description: "Send estimates directly to customers via email. They can view and approve right from their phone or computer."
    },
    {
      icon: CheckCircle,
      title: "One-Click Approval",
      description: "Customers approve estimates with a single click. Approved estimates automatically convert to scheduled jobs."
    }
  ];

  const invoiceFeatures = [
    {
      icon: RefreshCw,
      title: "Automatic Generation",
      description: "Invoices are created automatically from completed jobs. No manual data entry required."
    },
    {
      icon: CreditCard,
      title: "Payment Processing",
      description: "Accept credit card payments online. Customers pay directly from the invoice."
    },
    {
      icon: Clock,
      title: "Payment Tracking",
      description: "Track paid, unpaid, and overdue invoices. Know exactly where your money is at all times."
    }
  ];

  const workflow = [
    { step: "1", title: "Create Estimate", description: "Add services and pricing for the property" },
    { step: "2", title: "Send to Customer", description: "Customer receives professional proposal instantly" },
    { step: "3", title: "Customer Approves", description: "One-click approval creates the scheduled job" },
    { step: "4", title: "Complete Work", description: "Crew completes job and marks it done" },
    { step: "5", title: "Invoice Generated", description: "Invoice created automatically from completed job" },
    { step: "6", title: "Get Paid", description: "Customer pays online or payment is recorded" }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <FileText className="h-4 w-4" />
                Core Feature
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Estimates & Invoicing
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Create professional proposals, get faster approvals, and streamline your billing. 
                From estimate to payment, it's all connected.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Estimate #1234</span>
                  <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">Approved</span>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Weekly Lawn Maintenance</span>
                    <span className="font-medium">$75.00</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Hedge Trimming (Monthly)</span>
                    <span className="font-medium">$150.00</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Spring Cleanup</span>
                    <span className="font-medium">$250.00</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-2xl text-primary">$475.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">From Estimate to Payment</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A seamless workflow that connects every step—no duplicate data entry, no lost paperwork.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {workflow.map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  {item.step}
                </div>
                {index < workflow.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-gray-200"></div>
                )}
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Estimates Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Professional Estimates</h2>
              <p className="text-lg text-gray-600 mb-8">
                Win more business with professional proposals that make you look great. 
                Customers can view and approve instantly.
              </p>
              <div className="space-y-6">
                {estimateFeatures.map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Estimate Sent!</p>
                  <p className="text-sm text-gray-500">Waiting for customer approval</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span className="text-gray-600">Professional formatting</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span className="text-gray-600">Mobile-friendly viewing</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span className="text-gray-600">One-click approval button</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span className="text-gray-600">Automatic job creation on approval</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invoicing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Streamlined Invoicing</h2>
              <p className="text-lg text-gray-600 mb-8">
                Stop chasing payments. Invoices are generated automatically and customers can pay online instantly.
              </p>
              <div className="space-y-6">
                {invoiceFeatures.map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:order-1 bg-emerald-50 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-semibold text-gray-900">Invoice #5678</span>
                  <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">Paid</span>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Lawn Service - June</span>
                    <span>$300.00</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Hedge Trimming</span>
                    <span>$150.00</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-4 flex justify-between items-center">
                  <span className="text-gray-900 font-semibold">Total Paid</span>
                  <span className="text-2xl font-bold text-emerald-600">$450.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Create Estimates with a Simple Command
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our AI Assistant can create estimates instantly. Just tell it what you need, 
                and it handles the rest—including sending it to the customer.
              </p>
              <ul className="space-y-4">
                {[
                  "Create estimates by describing the job",
                  "AI suggests pricing based on service history",
                  "Ask about outstanding invoices instantly",
                  "Get payment status updates in seconds"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/ai-assistant">
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                    Learn About AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-violet-50 rounded-lg p-4 ml-8">
                  <p className="text-sm text-gray-700">"Create an estimate for weekly lawn mowing at 123 Oak Street for $85"</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mr-8">
                  <p className="text-sm text-gray-700">I'll create an estimate for John Smith at 123 Oak Street:</p>
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm font-medium">Weekly Lawn Mowing</p>
                    <p className="text-sm text-gray-500">$85.00/visit</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">Would you like me to create this estimate?</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <span className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg">Yes, create it</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Get Paid Faster
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Professional estimates and streamlined invoicing mean faster approvals and quicker payments.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default EstimatesInvoicingPage;
